import { FC, useCallback, useEffect, useMemo, useState } from "react";
import FormFieldComponent from "../../../../../../../common/components/form/field/form-field.component";
import InputComponent from "../../../../../../../common/components/form/input/input.component";
import SelectOption from "../../../../../../../common/components/form/select/common/option/select-option";
import Column from "../../../../../../../common/components/grid/column";
import Row from "../../../../../../../common/components/grid/row";
import MapComponent from "../../../../../../../common/components/map/map.component";
import ModalComponent from "../../../../../../../common/components/modal/modal.component";
import appTranslationsHelper from "../../../../../../../languages/app-translations.helper";
import signUpDriverFormHelper from "../../../common/sign-up-driver-form.helper";
import SignUpDriverAddressType from "../../../common/types/sign-up-driver-address-type";
import debounce from "lodash/debounce";
import MapMarker from "../../../../../../../common/components/map/types/map-marker";
import ButtonComponent from "../../../../../../../common/components/button/button.component";
import searchAddressApiService from "../../../../../../../common/utils/search-address/search-address.service";
import signUpDriverUserAddressAddFactory from "./common/factory/sign-up-driver-user-address-add.factory";
import formValidationService from "../../../../../../../common/utils/validation/form-validation.service";
import SignUpDriverFormUserAddress from "../../../common/types/sign-up-driver-form-user-address";
import signUpDriverUserAddressFormValidationService from "./sign-up-driver-user-address-form-validation.service";
import MapCoordinate from "../../../../../../../common/components/map/types/map-coordinate";
import { useSignUpDriverContext } from "../../../common/context/sign-up-driver.context";
import SearchAddressResponse from "../../../../../../../common/utils/search-address/search-address.response";
import SignUpAddressProposition from "../../../../common/types/sign-up-address-proposition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import SignUpAddressSelectOption from "../../../../common/types/sign-up-address-select-option";
import SingleSelectComponent from "../../../../../../../common/components/form/select/single-select/single-select.component";

type SignUpDriverUserAddressAddProps = {
  isModalOpen: boolean;
  onCloseClick: () => void;
};

const SignUpDriverUserAddressAddComponent: FC<
  SignUpDriverUserAddressAddProps
> = (props) => {
  const { addUserDataAddress } = useSignUpDriverContext();

  const [mapRefreshFlag, setMapRefreshFlag] = useState(false);
  const [refreshMapFitFlag, setRefreshMapFitFlag] = useState(false);
  const [addressSearchQuery, setAddressSearchQuery] = useState("");
  const [isAddressFetching, setIsAddressFetching] = useState(false);
  const [addressSelectOptions, setAddressSelectOptions] = useState<
    SignUpAddressSelectOption[]
  >([]);
  const [selectedAddressOption, setSelectedAddressOption] =
    useState<SignUpAddressSelectOption | null>(null);
  const [addressType, setAddressType] =
    useState<SelectOption<SignUpDriverAddressType> | null>(null);
  const addressTypeSelectOptions: SelectOption<SignUpDriverAddressType>[] =
    useMemo(() => signUpDriverFormHelper.getAddressTypeSelectOptions(), []);

  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [town, setTown] = useState("");
  const [description, setDescription] = useState("");
  const [validationResults, setValidationResults] = useState({
    addressType: formValidationService.defaultValidationResult,
    street: formValidationService.defaultValidationResult,
    houseNumber: formValidationService.defaultValidationResult,
    apartmentNumber: formValidationService.defaultValidationResult,
    zipCode: formValidationService.defaultValidationResult,
    country: formValidationService.defaultValidationResult,
    town: formValidationService.defaultValidationResult,
    description: formValidationService.defaultValidationResult,
    map: formValidationService.defaultValidationResult,
  });

  const [mapMarker, setMapMarker] = useState<MapMarker | null>(null);
  const [selectedCandidatureMapMarker, setSelectedCandidatureMapMarker] =
    useState<MapMarker | null>(null);
  const [selectedAddressCandidature, setSelectedAddressCandidature] =
    useState<SignUpAddressProposition | null>(null);

  const refreshMapFit = () => {
    setRefreshMapFitFlag((curr) => !curr);
  };

  const isAddressDetailsFormFieldEnabled = !!selectedAddressOption || mapMarker;

  const userDataAddNewAddressFormTranslations =
    appTranslationsHelper.getDriverSignUpTranslations().userDataForm
      .addNewAddressForm;

  const fetchResolvedAddress = (
    addressSearchQuery: string
  ): Promise<SearchAddressResponse> => {
    return searchAddressApiService.searchByQuery(addressSearchQuery);
  };

  const getAddressSelectOptions = (addressSearchQuery: string) => {
    setIsAddressFetching(true);

    fetchResolvedAddress(addressSearchQuery)
      .then((response) => {
        const selectOptions =
          signUpDriverUserAddressAddFactory.createAddressSelectOptions(
            response
          );

        setAddressSelectOptions(selectOptions);
      })
      .finally(() => {
        setIsAddressFetching(false);
      });
  };

  const getDebounceAddressSelectOptions = useCallback(
    debounce((addressSearchQuery: string) => {
      getAddressSelectOptions(addressSearchQuery);
    }, 300),
    []
  );

  const clearForm = () => {
    setAddressSelectOptions([]);
    setSelectedAddressOption(null);
    setAddressSearchQuery("");
    setAddressType(null);
    setStreet("");
    setHouseNumber("");
    setApartmentNumber("");
    setZipCode("");
    setCountry("");
    setTown("");
    setDescription("");
    setMapMarker(null);
    setValidationResults({
      addressType: formValidationService.defaultValidationResult,
      street: formValidationService.defaultValidationResult,
      houseNumber: formValidationService.defaultValidationResult,
      apartmentNumber: formValidationService.defaultValidationResult,
      zipCode: formValidationService.defaultValidationResult,
      country: formValidationService.defaultValidationResult,
      town: formValidationService.defaultValidationResult,
      description: formValidationService.defaultValidationResult,
      map: formValidationService.defaultValidationResult,
    });
    setMapRefreshFlag((curr) => !curr);
    setSelectedCandidatureMapMarker(null);
    setSelectedAddressCandidature(null);
  };

  useEffect(() => {
    if (props.isModalOpen) {
      return;
    }

    clearForm();
  }, [props.isModalOpen]);

  useEffect(() => {
    if (!selectedAddressOption) {
      clearForm();
      return;
    }

    const streetValue = selectedAddressOption.value.street ?? "";
    const houseNumberValue = selectedAddressOption.value.houseNumber ?? "";
    const zipCodeValue = selectedAddressOption.value.zipCode ?? "";
    const countryValue = selectedAddressOption.value.country;
    const townValue = selectedAddressOption.value.town ?? "";

    setStreet(streetValue);
    setHouseNumber(houseNumberValue);
    setApartmentNumber("");
    setZipCode(zipCodeValue);
    setCountry(countryValue);
    setTown(townValue);
    setMapMarker({
      coordinate: {
        latitude: selectedAddressOption.value.latitude,
        longitude: selectedAddressOption.value.longitude,
      },
      isDraggable: true,
    });

    validateStreet(streetValue);
    validateHouseNumber(houseNumberValue);
    validateZipCode(zipCodeValue);
    validateCountry(countryValue);
    validateTown(townValue);

    refreshMapFit();
  }, [selectedAddressOption]);

  useEffect(() => {
    if (!addressSearchQuery) {
      return;
    }

    getDebounceAddressSelectOptions(addressSearchQuery);
  }, [addressSearchQuery]);

  const validateAddressType = (
    value: SelectOption<SignUpDriverAddressType> | null = addressType
  ) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateAddressType(value);

    setValidationResults((curr) => ({
      ...curr,
      addressType: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateStreet = (_street: string = street) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateStreet(_street);

    setValidationResults((curr) => ({
      ...curr,
      street: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateHouseNumber = (_houseNumber: string = houseNumber) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateHouseNumber(
        _houseNumber
      );

    setValidationResults((curr) => ({
      ...curr,
      houseNumber: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateApartmentNumber = () => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateApartmentNumber(
        apartmentNumber
      );

    setValidationResults((curr) => ({
      ...curr,
      apartmentNumber: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateZipCode = (_zipCode: string = zipCode) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateZipCode(_zipCode);

    setValidationResults((curr) => ({
      ...curr,
      zipCode: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateCountry = (_country: string = country) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateCountry(_country);

    setValidationResults((curr) => ({
      ...curr,
      country: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateTown = (_town: string = town) => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateTown(_town);

    setValidationResults((curr) => ({
      ...curr,
      town: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateDescription = () => {
    const validationResult =
      signUpDriverUserAddressFormValidationService.validateDescription(
        description
      );

    setValidationResults((curr) => ({
      ...curr,
      description: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateForm = () => {
    const isAddressTypeValid = validateAddressType();
    const isStreetValid = validateStreet();
    const isHouseNumberValid = validateHouseNumber();
    const isApartmentNumberValid = validateApartmentNumber();
    const isTownValid = validateTown();
    const isZipCodeValid = validateZipCode();
    const isCountryValid = validateCountry();
    const isDescriptionValid = validateDescription();

    const isFormValid =
      isAddressTypeValid &&
      isStreetValid &&
      isHouseNumberValid &&
      isApartmentNumberValid &&
      isTownValid &&
      isZipCodeValid &&
      isCountryValid &&
      isDescriptionValid;

    return isFormValid;
  };

  const isSubmitButtonEnabled = true;

  const addNewAddress = () => {
    if (!mapMarker || !addressType) {
      return;
    }

    const newAddress: SignUpDriverFormUserAddress = {
      apartmentNumber,
      country,
      description,
      houseNumber,
      latitude: mapMarker.coordinate.latitude,
      longitude: mapMarker.coordinate.longitude,
      street,
      town,
      type: addressType.value,
      zipCode,
    };

    addUserDataAddress(newAddress);
  };

  const onSubmitButtonClick = () => {
    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    props.onCloseClick();
    addNewAddress();
  };

  const updateSelectedAddressCandidature = () => {
    if (!selectedCandidatureMapMarker) {
      setSelectedAddressCandidature(null);
      return;
    }

    const searchQuery = `${selectedCandidatureMapMarker.coordinate.latitude}, ${selectedCandidatureMapMarker.coordinate.longitude}`;
    fetchResolvedAddress(searchQuery).then((response) => {
      if (!response.length) {
        console.error(`No location found`);
        return;
      }

      const foundLocation = response[0];

      const address =
        signUpDriverUserAddressAddFactory.createAddress(foundLocation);

      setSelectedAddressCandidature(address);
    });
  };

  useEffect(() => {
    updateSelectedAddressCandidature();
  }, [selectedCandidatureMapMarker]);

  const handleLongMapClick = (coordinate: MapCoordinate) => {
    setSelectedCandidatureMapMarker({
      coordinate,
      isDraggable: true,
    });
  };

  const acceptAddressCandidature = () => {
    if (!selectedAddressCandidature || !selectedCandidatureMapMarker) {
      return;
    }

    setStreet(selectedAddressCandidature.street ?? "");
    setHouseNumber(selectedAddressCandidature.houseNumber ?? "");
    setApartmentNumber("");
    setZipCode(selectedAddressCandidature.zipCode ?? "");
    setCountry(selectedAddressCandidature.country);
    setTown(selectedAddressCandidature.town ?? "");
    setSelectedAddressCandidature(null);
    setMapMarker({ ...selectedCandidatureMapMarker });
    setSelectedCandidatureMapMarker(null);
    refreshMapFit();
  };

  const isMessageBoxVisible = !!selectedAddressCandidature;

  const checkIsFormModified = (): boolean => {
    if (
      mapMarker ||
      selectedCandidatureMapMarker ||
      addressSearchQuery !== "" ||
      street !== "" ||
      houseNumber !== "" ||
      apartmentNumber !== "" ||
      zipCode !== "" ||
      country !== "" ||
      town !== "" ||
      description !== ""
    ) {
      return true;
    }

    return false;
  };

  const onMarkerCoordinateChange = (newMarker: MapMarker) => {
    setMapMarker(newMarker);
  };

  const onTemporaryMarkerCoordinateChange = (newMarker: MapMarker) => {
    setSelectedCandidatureMapMarker({
      ...newMarker,
    });
  };

  const onAddressTypeChange = (
    value: SelectOption<SignUpDriverAddressType>
  ) => {
    setAddressType(value);
    validateAddressType(value);
  };

  const isFormModified = checkIsFormModified();

  const mapMessageBox = selectedAddressCandidature ? (
    <div style={{ display: `flex`, maxHeight: 40 }}>
      <div
        style={{
          fontSize: 12,
          textOverflow: "ellipsis",
          overflow: "hidden",
          display: `-webkit-box`,
          WebkitLineClamp: 2,
          WebkitBoxOrient: `vertical`,
        }}
        title={selectedAddressCandidature.displayName}
      >
        {selectedAddressCandidature.displayName}
      </div>
      <div>
        <ButtonComponent onClick={acceptAddressCandidature} type="success">
          <FontAwesomeIcon icon={faCheck} />
        </ButtonComponent>
      </div>
    </div>
  ) : null;

  return (
    <ModalComponent
      header={{ title: userDataAddNewAddressFormTranslations.title }}
      classNames={{ root: "sign_up_user_add_address" }}
      isOpen={props.isModalOpen}
      onClose={props.onCloseClick}
      actions={[
        <ButtonComponent
          onClick={onSubmitButtonClick}
          type="primary"
          isDisabled={!isSubmitButtonEnabled}
          title={userDataAddNewAddressFormTranslations.submitButtonTitle}
          idForTesting="user-data-add-new-address-submit-button"
        >
          {userDataAddNewAddressFormTranslations.submitButtonText}
        </ButtonComponent>,
      ]}
      closeConfirmation={{
        title: userDataAddNewAddressFormTranslations.closeConfirmation.title,
        messageText:
          userDataAddNewAddressFormTranslations.closeConfirmation.textMessage,
        shouldBeVisibleOnCloseClick: isFormModified,
      }}
    >
      <div style={{ fontSize: 14, padding: `0 0 20px 0` }}>
        {userDataAddNewAddressFormTranslations.tipText}
      </div>
      <Row>
        <Column xl={6}>
          <FormFieldComponent
            label={userDataAddNewAddressFormTranslations.searchLabel}
            classNames={{ root: "mt-0" }}
          >
            <SingleSelectComponent
              placeholder={
                userDataAddNewAddressFormTranslations.searchPlaceholder
              }
              options={addressSelectOptions}
              value={selectedAddressOption}
              isLoading={isAddressFetching}
              inputValue={addressSearchQuery}
              onInputChange={setAddressSearchQuery}
              isClearable
              onChange={(option) => setSelectedAddressOption(option)}
              isSearchable
              menuPlacement="bottom"
              filterFunction={() => true}
              idForTesting="user-data-add-new-address-search-select"
              menuPosition="fixed"
            />
          </FormFieldComponent>
          <FormFieldComponent
            label={userDataAddNewAddressFormTranslations.addressTypeLabel}
            isRequired
            errorMessage={validationResults.addressType.errorMessage}
          >
            <SingleSelectComponent
              placeholder={
                userDataAddNewAddressFormTranslations.addressTypePlaceholder
              }
              options={addressTypeSelectOptions}
              value={addressType}
              onChange={(option) => onAddressTypeChange(option!)}
              menuPlacement="bottom"
              isSearchable
              hasError={!!validationResults.addressType.errorMessage}
              idForTesting="user-data-add-new-address-address-type-select"
            />
          </FormFieldComponent>
          <Row>
            <Column xl={6}>
              <FormFieldComponent
                label={userDataAddNewAddressFormTranslations.streetLabel}
                isRequired
                errorMessage={validationResults.street.errorMessage}
              >
                <InputComponent
                  value={street}
                  onChange={setStreet}
                  placeholder={
                    userDataAddNewAddressFormTranslations.streetPlaceholder
                  }
                  isDisabled={!isAddressDetailsFormFieldEnabled}
                  hasError={!!validationResults.street.errorMessage}
                  onBlur={validateStreet}
                  idForTesting="user-data-add-new-address-street-input"
                />
              </FormFieldComponent>
            </Column>
            <Column xl={3}>
              <FormFieldComponent
                label={userDataAddNewAddressFormTranslations.houseNumberLabel}
                isRequired
                errorMessage={validationResults.houseNumber.errorMessage}
              >
                <InputComponent
                  value={houseNumber}
                  onChange={setHouseNumber}
                  placeholder={
                    userDataAddNewAddressFormTranslations.houseNumberPlaceholder
                  }
                  isDisabled={!isAddressDetailsFormFieldEnabled}
                  hasError={!!validationResults.houseNumber.errorMessage}
                  onBlur={validateHouseNumber}
                  idForTesting="user-data-add-new-address-house-number-input"
                />
              </FormFieldComponent>
            </Column>
            <Column xl={3}>
              <FormFieldComponent
                label={
                  userDataAddNewAddressFormTranslations.apartmentNumberLabel
                }
                errorMessage={validationResults.apartmentNumber.errorMessage}
              >
                <InputComponent
                  value={apartmentNumber}
                  onChange={setApartmentNumber}
                  placeholder={
                    userDataAddNewAddressFormTranslations.apartmentNumberPlaceholder
                  }
                  isDisabled={!isAddressDetailsFormFieldEnabled}
                  hasError={!!validationResults.apartmentNumber.errorMessage}
                  onBlur={validateApartmentNumber}
                  idForTesting="user-data-add-new-address-apartment-number-input"
                />
              </FormFieldComponent>
            </Column>
          </Row>
          <Row>
            <Column xl={6}>
              <FormFieldComponent
                label={userDataAddNewAddressFormTranslations.townLabel}
                classNames={{ root: "mt-0" }}
                isRequired
                errorMessage={validationResults.town.errorMessage}
              >
                <InputComponent
                  value={town}
                  onChange={setTown}
                  placeholder={
                    userDataAddNewAddressFormTranslations.townPlaceholder
                  }
                  isDisabled={!isAddressDetailsFormFieldEnabled}
                  hasError={!!validationResults.town.errorMessage}
                  onBlur={validateTown}
                  idForTesting="user-data-add-new-address-town-input"
                />
              </FormFieldComponent>
            </Column>
            <Column xl={6}>
              <FormFieldComponent
                label={userDataAddNewAddressFormTranslations.zipCodeLabel}
                classNames={{ root: "mt-0" }}
                isRequired
                errorMessage={validationResults.zipCode.errorMessage}
              >
                <InputComponent
                  value={zipCode}
                  onChange={setZipCode}
                  placeholder={
                    userDataAddNewAddressFormTranslations.zipCodePlaceholder
                  }
                  isDisabled={!isAddressDetailsFormFieldEnabled}
                  hasError={!!validationResults.zipCode.errorMessage}
                  onBlur={validateZipCode}
                  idForTesting="user-data-add-new-address-zip-code-input"
                />
              </FormFieldComponent>
            </Column>
          </Row>
          <FormFieldComponent
            label={userDataAddNewAddressFormTranslations.countryLabel}
            classNames={{ root: "mt-0" }}
            isRequired
            errorMessage={validationResults.country.errorMessage}
          >
            <InputComponent
              value={country}
              onChange={setCountry}
              placeholder={
                userDataAddNewAddressFormTranslations.countryPlaceholder
              }
              isDisabled={!isAddressDetailsFormFieldEnabled}
              hasError={!!validationResults.country.errorMessage}
              onBlur={validateCountry}
              idForTesting="user-data-add-new-address-country-input"
            />
          </FormFieldComponent>
          <FormFieldComponent
            label={userDataAddNewAddressFormTranslations.descriptionLabel}
            classNames={{ root: "mt-0" }}
            errorMessage={validationResults.description.errorMessage}
          >
            <InputComponent
              value={description}
              onChange={setDescription}
              placeholder={
                userDataAddNewAddressFormTranslations.descriptionPlaceholder
              }
              isDisabled={!isAddressDetailsFormFieldEnabled}
              hasError={!!validationResults.description.errorMessage}
              onBlur={validateDescription}
              idForTesting="user-data-add-new-address-description-input"
            />
          </FormFieldComponent>
        </Column>
        <Column xl={6}>
          <FormFieldComponent
            label={userDataAddNewAddressFormTranslations.mapLabel}
            classNames={{
              root: "mt-0 sign_up_user_add_address_map_form_field",
            }}
            errorMessage={validationResults.map.errorMessage}
          >
            <MapComponent
              key={`add-address-map-${mapRefreshFlag}`}
              markers={mapMarker ? [mapMarker] : []}
              onMarkerCoordinateChange={onMarkerCoordinateChange}
              temporaryMarkers={
                selectedCandidatureMapMarker
                  ? [selectedCandidatureMapMarker]
                  : []
              }
              onTemporaryMarkerCoordinateChange={
                onTemporaryMarkerCoordinateChange
              }
              onMapLongClick={handleLongMapClick}
              messageBox={mapMessageBox}
              isMessageBoxVisible={isMessageBoxVisible}
              refreshFitFlag={refreshMapFitFlag}
            />
          </FormFieldComponent>
        </Column>
      </Row>
    </ModalComponent>
  );
};

export default SignUpDriverUserAddressAddComponent;
